import type { PartnerOrganization, PartnerOrganizationAddress } from "@entities";
import { partnerOrganizationAddressRepo } from "@repositories";
import { hasValidCoordinates } from "@shared/helpers/geo";
import {
  draftsFromPartnerOrganization as draftsFromPartnerOrganizationCore,
  emptyPartnerOrgAddressDraft,
  filledAddressDrafts,
  primaryAddressFromDrafts,
  type PartnerOrgAddressDraft,
} from "@shared/helpers/partner-organization-address-drafts";

export type { PartnerOrgAddressDraft };
export {
  emptyPartnerOrgAddressDraft,
  filledAddressDrafts,
  primaryAddressFromDrafts,
};

export function draftsFromPartnerOrganization(
  organization: PartnerOrganization | null,
): PartnerOrgAddressDraft[] {
  return draftsFromPartnerOrganizationCore(organization);
}

export async function syncPartnerOrganizationAddresses(
  partnerOrganizationId: string,
  drafts: PartnerOrgAddressDraft[],
): Promise<PartnerOrganizationAddress[]> {
  const existing = await partnerOrganizationAddressRepo.find({
    where: { partnerOrganizationId },
  });
  const existingIds = new Set(existing.map((row) => row.id));
  const keptIds = new Set<string>();
  const saved: PartnerOrganizationAddress[] = [];
  let sortOrder = 0;

  for (const draft of filledAddressDrafts(drafts)) {
    const payload = {
      partnerOrganizationId,
      address: draft.address.trim(),
      label: draft.label.trim() || undefined,
      coordinates: (hasValidCoordinates(draft.coordinates)
        ? draft.coordinates
        : null) as { lat: number; lng: number } | undefined,
      sortOrder,
    };
    sortOrder += 1;
    if (draft.id && existingIds.has(draft.id)) {
      const updated = await partnerOrganizationAddressRepo.update(
        draft.id,
        payload,
      );
      keptIds.add(draft.id);
      saved.push(updated);
    } else {
      const created = await partnerOrganizationAddressRepo.insert(payload);
      keptIds.add(created.id);
      saved.push(created);
    }
  }

  await Promise.all(
    existing
      .filter((row) => !keptIds.has(row.id))
      .map((row) => partnerOrganizationAddressRepo.delete(row.id)),
  );

  return saved;
}
